import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MangaCard from "@/components/feature/MangaCard";
import { useTheme } from "@/hooks/useTheme";
import { categories, mangaList } from "@/mocks/manga";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { isDark, toggleTheme } = useTheme();

  const category = categories.find((c) => c.slug === slug);
  const filteredManga = mangaList.filter((m) => m.category === slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
          <h2 className="text-xl text-foreground-950 mb-2">分类未找到</h2>
          <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">返回首页</Link>
        </div>
      </div>
    );
  }

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": `http://biensur.cn/category/${slug}/#collectionpage`,
        url: `http://biensur.cn/category/${slug}/`,
        name: `${category.name}漫画 - 漫蛙免费在线漫画`,
        description: `漫蛙${category.name}漫画专区，海量免费${category.name}漫画资源，高清画质，每日更新。`,
        isPartOf: { "@id": "http://biensur.cn/#website" },
        about: { "@id": "http://biensur.cn/#organization" },
      },
      {
        "@type": "BreadcrumbList",
        "@id": `http://biensur.cn/category/${slug}/#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "http://biensur.cn/" },
          { "@type": "ListItem", position: 2, name: `${category.name}漫画`, item: `http://biensur.cn/category/${slug}/` },
        ],
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar isDark={isDark} toggleTheme={toggleTheme} />

        {/* Hero */}
        <section className="relative h-[280px] md:h-[360px] w-full overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Artistic%20anime%20style%20illustration%20with%20dynamic%20action%20composition%2C%20Japanese%20manga%20inspired%20art%20with%20bold%20color%20splashes%2C%20energetic%20and%20vibrant%20atmosphere%2C%20clean%20composition%20with%20soft%20gradient%20background%2C%20cinematic%20lighting&width=1800&height=600&seq=category-hero-bg&orientation=landscape"
            alt={`漫蛙${category.name}漫画`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          <div className="relative z-10 h-full max-w-7xl mx-auto px-4 md:px-6 flex flex-col justify-center">
            <nav className="flex items-center gap-2 text-white/70 text-xs mb-4 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-white transition-colors whitespace-nowrap">首页</Link>
              <span>/</span>
              <span className="text-white font-medium">{category.name}漫画</span>
            </nav>
            <h1 className="text-white font-heading text-3xl md:text-4xl font-bold mb-3">
              {category.name}漫画
            </h1>
            <p className="text-white/80 text-sm md:text-base max-w-lg">
              漫蛙{category.name}漫画专区，海量免费{category.name}漫画资源，高清画质，每日更新。最新章节同步追更，享受极致阅读体验。
            </p>
          </div>
        </section>

        {/* Category tabs */}
        <section className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 py-4 overflow-x-auto flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    cat.slug === slug
                      ? "bg-primary-500 text-white"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Manga grid */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <p className="text-sm text-foreground-500">
                共找到 <strong className="text-foreground-950">{filteredManga.length}</strong> 部{category.name}漫画
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500">排序：</span>
                <select className="text-xs bg-background-100 border border-background-200/70 rounded-md px-2 py-1.5 text-foreground-700 outline-none cursor-pointer">
                  <option>最新更新</option>
                  <option>最受欢迎</option>
                  <option>评分最高</option>
                </select>
              </div>
            </div>

            {filteredManga.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
                {filteredManga.map((manga) => (
                  <MangaCard
                    key={manga.id}
                    id={manga.id}
                    title={manga.title}
                    author={manga.author}
                    description={manga.description}
                    chapters={manga.chapters}
                    status={manga.status}
                    rating={manga.rating}
                    views={manga.views}
                    updateTime={manga.updateTime}
                    imageUrl={manga.imageUrl}
                    imageUrlThumb={manga.imageUrlThumb}
                    variant="default"
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <i className="ri-emotion-sad-line text-4xl text-foreground-300 mb-3 block"></i>
                <p className="text-foreground-500">暂无该分类下的漫画，敬请期待！</p>
              </div>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}